import { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { toast } from "sonner";
import { Angry, Frown, Meh, Smile, Laugh, Zap, ClipboardCheck, Pencil, ChevronDown, Sunrise, Moon } from "lucide-react";

// Mood colour scale: 1 red (sad) → 3 grey (neutral) → 5 green (top joy).
const MOODS = [
  { v: 1, Icon: Angry, icon: "text-red-500", sel: "border-red-400 bg-red-50" },
  { v: 2, Icon: Frown, icon: "text-orange-400", sel: "border-orange-400 bg-orange-50" },
  { v: 3, Icon: Meh, icon: "text-slate-400", sel: "border-slate-400 bg-slate-100" },
  { v: 4, Icon: Smile, icon: "text-lime-500", sel: "border-lime-400 bg-lime-50" },
  { v: 5, Icon: Laugh, icon: "text-emerald-500", sel: "border-emerald-400 bg-emerald-50" },
];

// Energy colour scale: 1 yellow (weak) rising to 5 red (very strong).
const ENERGY = [
  { v: 1, icon: "text-yellow-400", fill: "fill-yellow-300", sel: "border-yellow-300 bg-yellow-50" },
  { v: 2, icon: "text-amber-400", fill: "fill-amber-300", sel: "border-amber-300 bg-amber-50" },
  { v: 3, icon: "text-orange-400", fill: "fill-orange-300", sel: "border-orange-300 bg-orange-50" },
  { v: 4, icon: "text-orange-600", fill: "fill-orange-500", sel: "border-orange-500 bg-orange-50" },
  { v: 5, icon: "text-red-500", fill: "fill-red-400", sel: "border-red-400 bg-red-50" },
];

const moodMetaOf = (v) => MOODS.find((m) => m.v === v);

function ZapRow({ energy, size = "h-4 w-4" }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Zap key={i} className={`${size} ${i <= energy ? `${ENERGY[i - 1].icon} ${ENERGY[i - 1].fill}` : "text-slate-200 dark:text-slate-700"}`} />
      ))}
    </span>
  );
}

/** One phase (check-in morning / check-out evening): form or done-summary. */
function PhaseSection({ t, tid, PhaseIcon, phaseTint, title, moodQ, saveLabel, initial, onSave }) {
  const [editing, setEditing] = useState(false);
  const [mood, setMood] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMood(initial.mood || 0);
    setEnergy(initial.energy || 0);
    setNote(initial.note || "");
    setEditing(false);
  }, [initial.done, initial.mood, initial.energy, initial.note]);

  const save = async () => {
    if (!mood || !energy) return;
    setSaving(true);
    try {
      await onSave(mood, energy, note);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const MoodIcon = (moodMetaOf(mood) || {}).Icon;
  const showForm = !initial.done || editing;

  return (
    <div data-testid={`${tid}-section`}>
      <div className="flex items-center gap-2">
        <PhaseIcon className={`h-4 w-4 ${phaseTint}`} />
        <p className="text-xs font-bold text-slate-700 dark:text-slate-200 flex-1">{title}</p>
        {initial.done && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`${tid}-edit-btn`}
            className="h-7 w-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition active:scale-90"
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {!showForm ? (
        <div className="mt-2 flex items-center gap-3 pl-6" data-testid={`${tid}-done-state`}>
          {MoodIcon && <MoodIcon className={`h-6 w-6 ${moodMetaOf(mood).icon}`} />}
          <ZapRow energy={energy} />
          {note ? <p className="text-xs text-slate-500 italic truncate flex-1">"{note}"</p> : null}
        </div>
      ) : (
        <div className="mt-2 space-y-3">
          <div>
            <p className="text-[11px] text-slate-500 mb-1.5">{moodQ}</p>
            <div className="flex gap-2">
              {MOODS.map(({ v, Icon, icon, sel }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMood(v)}
                  data-testid={`${tid}-mood-${v}`}
                  className={`h-11 flex-1 rounded-xl border flex items-center justify-center transition active:scale-90 ${mood === v ? sel : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                  aria-label={`Mood ${v}`}
                >
                  <Icon className={`h-6 w-6 ${icon}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 mb-1.5">{t("cliDash.checkinEnergyQ")}</p>
            <div className="flex gap-2">
              {ENERGY.map(({ v, icon, fill, sel }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setEnergy(v)}
                  data-testid={`${tid}-energy-${v}`}
                  className={`h-11 flex-1 rounded-xl border flex items-center justify-center transition active:scale-90 ${energy >= v ? sel : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60"}`}
                  aria-label={`Energy ${v}`}
                >
                  <Zap className={`h-5 w-5 ${energy >= v ? `${icon} ${fill}` : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder={t("cliDash.checkinNotePh")}
            data-testid={`${tid}-note-input`}
            className="w-full h-10 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0f1213] px-3 text-sm focus:outline-none focus:border-emerald-400"
          />
          <button
            type="button"
            onClick={save}
            disabled={!mood || !energy || saving}
            data-testid={`${tid}-save-btn`}
            className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-40 active:scale-[0.98] transition"
          >
            {saving ? "…" : saveLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default function DailyCheckinCard({ t, date, onSaved, className = "" }) {
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    api.get(`/client/checkin?date=${date}`)
      .then(({ data: d }) => {
        if (!alive) return;
        setData(d || {});
        setOpen(false);
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [date]);

  const savePhase = (phase) => async (mood, energy, note) => {
    try {
      await api.post("/client/checkin", { date, phase, mood, energy, note: note.trim() || null });
      toast.success(phase === "out" ? t("cliDash.checkoutSaved") : t("cliDash.checkinSaved"));
      setData((d) => phase === "out"
        ? { ...d, done_out: true, mood_out: mood, energy_out: energy, note_out: note }
        : { ...d, done: true, mood, energy, note });
      onSaved?.();
    } catch {
      toast.error(t("common.error"));
      throw new Error("save failed");
    }
  };

  if (!loaded) return null;

  const InMood = (moodMetaOf(data.mood) || {}).Icon;
  const OutMood = (moodMetaOf(data.mood_out) || {}).Icon;

  return (
    <div className={`rounded-3xl bg-white dark:bg-[#141719] border border-slate-100 dark:border-slate-800 p-4 shadow-sm ${className}`} data-testid="daily-checkin-card">
      {/* Collapsible header — tap to open/close */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="checkin-toggle"
        className="w-full flex items-center gap-2 text-left"
      >
        <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
          <ClipboardCheck className="h-4 w-4" />
        </div>
        <p className="text-sm font-bold text-slate-900 dark:text-slate-100 flex-1">{t("cliDash.checkinOutTitle")}</p>
        {data.done && (
          <span className="flex items-center gap-1 mr-1" data-testid="checkin-summary">
            <Sunrise className="h-3.5 w-3.5 text-amber-400" />
            {InMood && <InMood className={`h-5 w-5 ${moodMetaOf(data.mood).icon}`} />}
          </span>
        )}
        {data.done_out && (
          <span className="flex items-center gap-1 mr-1" data-testid="checkout-summary">
            <Moon className="h-3.5 w-3.5 text-indigo-400" />
            {OutMood && <OutMood className={`h-5 w-5 ${moodMetaOf(data.mood_out).icon}`} />}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <PhaseSection
            t={t}
            tid="checkin"
            PhaseIcon={Sunrise}
            phaseTint="text-amber-400"
            title={t("cliDash.checkinSection")}
            moodQ={t("cliDash.checkinMoodQ")}
            saveLabel={t("cliDash.checkinSave")}
            initial={{ done: !!data.done, mood: data.mood, energy: data.energy, note: data.note }}
            onSave={savePhase("in")}
          />
          <div className="h-px bg-slate-100 dark:bg-slate-800" />
          <PhaseSection
            t={t}
            tid="checkout"
            PhaseIcon={Moon}
            phaseTint="text-indigo-400"
            title={t("cliDash.checkoutSection")}
            moodQ={t("cliDash.checkoutMoodQ")}
            saveLabel={t("cliDash.checkoutSave")}
            initial={{ done: !!data.done_out, mood: data.mood_out, energy: data.energy_out, note: data.note_out }}
            onSave={savePhase("out")}
          />
        </div>
      )}
    </div>
  );
}
